import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { CategoryAd } from '@/components/Ads/CategoryAd';
import ManageButton from '@/components/Button/ManageButton';
import { DismissableGetMobileBannerAlt } from '@/components/DismissableGetMobileBanner/DismissableGetMobileBannerAlt';
import PageTitle from '@/components/PageTitle/PageTitle';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { MyFavoritesFilterDrawer } from '@/pages/MyItems/MyFavorites/components/MyFavoritesFilterDrawer/MyFavoritesFilterDrawer';
import { useAppSelector } from '@/redux/hooks';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getMyBidsUrl, getNotificationsUrl, getSavedItemsUrl } from '@/utils/urls';

import { MyItemsTab } from '../../types/MyItemsPage.types';
import MyItemsShell from '../MyItemsShell/MyItemsShell';
import { MyItemsShellSection } from '../MyItemsShell/MyItemsShell.types';
import { useMyItemsHub } from '../MyItemsShell/useMyItemsHub';

type Props = {
    children: ReactNode;
    tab: MyItemsTab;
};

/**
 * The "my items" page is actually two different pages: "my favorites" and "my bids"
 * where the routing to either page is controlled by tabs
 */
const MyItemsPageContainer = ({ children, tab }: Props) => {
    const ssrWidth = useAppSelector(getInitialWidth);
    const isDesktopView = useIsWiderThan(990, ssrWidth);
    const navigate = useNavigate();
    const formatMessage = useAppFormatMessage();
    const { ownsBanner, showHub, showMobileTabs } = useMyItemsHub();

    // 'favorites-page' is unfortunately the ad id
    const adId = tab === MyItemsTab.MyFavorites ? 'favorites-page' : MyItemsTab.MyBids;
    const manageUrl = getNotificationsUrl('searches');

    /**
     * Navigate to the my-bids or my-favorites via tabs
     */
    const handleTabsOnChange = (contentId: string) => {
        if (contentId === MyItemsTab.MyBids && tab !== MyItemsTab.MyBids) {
            navigate(getMyBidsUrl());
        } else if (contentId === MyItemsTab.MyFavorites && tab !== MyItemsTab.MyFavorites) {
            navigate(getSavedItemsUrl());
        }
    };

    const adPageName = `leaderboard-${adId}`;
    const ad = (
        <CategoryAd
            adType={LeaderBoardAdTypes[adId]}
            className={showHub ? 'pl-xl' : undefined}
            pageName={adPageName}
        />
    );

    const filterDrawer = (
        <MyFavoritesFilterDrawer
            // In the shell's tab row the section content comes later in the DOM and would paint over the
            // button's bottom edge; z on the button only (not its wrapper) so the drawer overlay isn't trapped
            buttonClassName={showMobileTabs ? 'relative z-[2]' : undefined}
        />
    );

    return (
        // Under treatment the shell renders the ad (top of its content column in hub mode)
        <MyItemsShell
            activeSection={tab === MyItemsTab.MyBids ? MyItemsShellSection.MyBids : MyItemsShellSection.Favorites}
            banner={ad}
            // Mobile treatment: the filter sits in the shell's tab row, where it sat in the legacy tab row
            tabsEndContent={!isDesktopView && tab === MyItemsTab.MyFavorites ? filterDrawer : undefined}
        >
            {!ownsBanner && ad}
            <div className="relative mx-auto flex w-full max-w-[1240px] flex-col self-center px-sm">
                {/* LAAC-2623: on mobile under treatment the shell renders the heading and section tabs */}
                {!showMobileTabs && (
                    <PageTitle
                        className="my-lg"
                        title={<>{formatMessage(['myItems', 'title'])}</>}
                    />
                )}
                <DismissableGetMobileBannerAlt className="mb-md" />
                <div className="relative">
                    <Tabs
                        // Hide (not unmount) the page's own tab row so a width flip after hydration
                        // doesn't remount the section; the shell's tabs replace it
                        className={showMobileTabs ? '*:[[role=tablist]]:hidden' : undefined}
                        currentTab={tab}
                        onTabChanged={handleTabsOnChange}
                        tabs={[
                            {
                                content: tab === MyItemsTab.MyFavorites ? children : null,
                                contentId: MyItemsTab.MyFavorites,
                                label: formatMessage(['myItems', 'favorites']),
                            },
                            {
                                content: tab === MyItemsTab.MyBids ? children : null,
                                contentId: MyItemsTab.MyBids,
                                label: formatMessage(['myItems', 'bids']),
                            },
                        ]}
                    />
                    {isDesktopView && (
                        <div className="absolute top-[-7px] right-0 flex items-center">
                            <ManageButton
                                hideManage
                                id="manageNotifications"
                                messageId="manageNotifications"
                                to={manageUrl}
                                withPopover
                            />
                        </div>
                    )}
                    {!isDesktopView && !showMobileTabs && tab === MyItemsTab.MyFavorites && (
                        <div className="absolute -top-xs right-0 flex items-center">{filterDrawer}</div>
                    )}
                </div>
            </div>
        </MyItemsShell>
    );
};

export default MyItemsPageContainer;
